import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useMutation } from '@tanstack/react-query';

import type { InFieldUserPreferences } from '@cognite/apm-client';

import { MutationKeys, QueryKeys } from '../../../../utils/queryKeys';

import { UseUpsertCurrentUserPreferencesContext } from './cdm-use-upsert-user-preferences.context';
import { upsertUserPreferences } from './mutation-functions/cdm-upsert-user-preferences';
import type { UserPreferencesUpdate } from './mutation-functions/user-preference';

export const useUpsertCurrentUserPreferences = () => {
  const {
    useUserServices,
    useQueryClient,
    makeToast,
    captureException,
    useCurrentUserContext,
  } = useContext(UseUpsertCurrentUserPreferencesContext);

  const { userPreferencesService } = useUserServices();
  const { user } = useCurrentUserContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation('activity');

  const { mutateAsync, isError, error } = useMutation<
    InFieldUserPreferences | undefined,
    Error,
    UserPreferencesUpdate
  >({
    mutationFn: async (userPreferencesUpdate: UserPreferencesUpdate) => {
      return upsertUserPreferences(
        userPreferencesUpdate,
        user,
        userPreferencesService.upsert.bind(userPreferencesService)
      );
    },
    onError: (err, variables) => {
      makeToast({
        type: 'danger',
        body: t(
          'failedToUpdateUserPreference',
          `Failed to update user preference ${variables.preference}`
        ),
      });
      captureException(err, {
        level: 'error',
        tags: {
          mutationKey: MutationKeys.UPSERT_CURRENT_USER_PREFERENCES,
        },
      });
    },
    onSuccess: async () => {
      await Promise.all([
        // Using refetchQueries to ensure fresh user data is in cache before re-render.
        // invalidateQueries only marks cache as stale without waiting for the refetch.
        queryClient.refetchQueries({ queryKey: [QueryKeys.USER] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.USER_PREFERENCES],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.INFIELD_LOCATION_CONFIG_FROM_SERVICE],
        }),
      ]);
    },
    mutationKey: [MutationKeys.UPSERT_CURRENT_USER_PREFERENCES],
  });

  return { mutateAsync, isError, error };
};
